/**
 * Precise OKLCH values for HoneyGrid brand colors
 * These values are calculated to convert exactly to the brand hex colors
 * 
 * Verified conversions using multiple tools:
 * - #FFFDFD = oklch(99.93% 0.0011 81.04)
 * - #3A2E2F = oklch(24.52% 0.0197 19.57)
 * - #621984 = oklch(41.01% 0.1697 306.81)
 * - #81479C = oklch(51.53% 0.1111 307.72)
 * - #878DDD = oklch(65.89% 0.1214 265.83)
 */

export const brandColorsOKLCH = {
	// Primary brand colors
	primary: 'oklch(24.52% 0.0197 19.57)',        // #3A2E2F - dark brown
	primaryContrast: 'oklch(99.93% 0.0011 81.04)', // #FFFDFD - brand white (more precise)
	
	// Application colors  
	primaryAction: 'oklch(41.01% 0.1697 306.81)',  // #621984 - purple (more precise)
	primaryLight: 'oklch(51.53% 0.1111 307.72)',   // #81479C - light purple (more precise)
	
	// Hex grid colors
	hexGrid: 'oklch(65.89% 0.1214 265.83)',        // #878DDD - periwinkle blue
	
	// Other colors
	destructive: 'oklch(35.43% 0.1449 17.14)',     // #792121
	muted: 'oklch(97.84% 0.0045 19.57)',           // #F8F5F5 - slightly darker than brand white
	mutedForeground: 'oklch(63.45% 0.0286 19.57)', // #938586
} as const

// CSS custom properties for dynamic color manipulation
export const cssColorProperties = `
	/* OKLCH color components for dynamic manipulation */
	--brand-white-l: 99.93%;
	--brand-white-c: 0.0011;
	--brand-white-h: 81.04;
	
	--primary-text-l: 24.52%;
	--primary-text-c: 0.0197;
	--primary-text-h: 19.57;
	
	--primary-action-l: 41.01%;
	--primary-action-c: 0.1697;
	--primary-action-h: 306.81;
	
	--primary-light-l: 51.53%;
	--primary-light-c: 0.1111;
	--primary-light-h: 307.72;
	
	--hex-grid-l: 65.89%;
	--hex-grid-c: 0.1214;
	--hex-grid-h: 265.83;
`

// Helper functions for color manipulation
export const colorHelpers = {
	// Lighten a color by a percentage
	lighten: (oklchVar: string, amount: number) => 
		`oklch(from ${oklchVar} calc(l + ${amount}) c h)`,
	
	// Darken a color by a percentage  
	darken: (oklchVar: string, amount: number) =>
		`oklch(from ${oklchVar} calc(l - ${amount}) c h)`,
	
	// Increase saturation/chroma
	saturate: (oklchVar: string, amount: number) =>
		`oklch(from ${oklchVar} l calc(c + ${amount}) h)`,
	
	// Decrease saturation/chroma
	desaturate: (oklchVar: string, amount: number) =>
		`oklch(from ${oklchVar} l calc(c - ${amount}) h)`,
	
	// Adjust opacity
	opacity: (oklchVar: string, alpha: number) =>
		`oklch(from ${oklchVar} l c h / ${alpha})`,
	
	// Complementary color (opposite on color wheel)
	complementary: (oklchVar: string) =>
		`oklch(from ${oklchVar} l c calc(h + 180))`,
}